import { ref } from 'vue'
import type { ExtendedMediaTrackCapabilities } from './useCameraOptimizer'
import type { CameraScore } from './useCameraSelector'

export interface DebugCameraInfo {
    allCameras: CameraScore[]
    selectedCamera: string
    capabilities: {
        width?: { max?: number; min?: number }
        height?: { max?: number; min?: number }
        zoom?: unknown
        torch?: boolean
        focusMode?: string[]
        focusDistance?: { min?: number; max?: number; step?: number }
        deviceId?: string
        facingMode?: string
    } | null
    currentSettings: {
        width?: number
        height?: number
        frameRate?: number
        focusMode?: string
        focusDistance?: number
        facingMode?: string
    } | null
    appliedConstraints: Record<string, unknown> | null
    deviceInfo: {
        userAgent: string
        isMobile: boolean
        isSecure: boolean
    }
    errors: string[]
}

// Глобальное (модульное) состояние, сохраняется между маршрутами
const debugInfo = ref<DebugCameraInfo>({
    allCameras: [],
    selectedCamera: '',
    capabilities: null,
    currentSettings: null,
    appliedConstraints: null,
    deviceInfo: {
        userAgent: navigator.userAgent,
        isMobile: false,
        isSecure: false,
    },
    errors: [],
})
export function useCameraDebug() {
    /**
     * Обновить информацию о всех доступных камерах
     */
    const updateCamerasList = (cameras: CameraScore[]) => {
        debugInfo.value.allCameras = cameras
    }

    /**
     * Обновить информацию о выбранной камере
     */
    const updateSelectedCamera = (track: MediaStreamTrack) => {
        const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities
        const settings = track.getSettings()
        const constraints = track.getConstraints()

        debugInfo.value.selectedCamera = track.label

        debugInfo.value.capabilities = {
            width: capabilities.width,
            height: capabilities.height,
            zoom: capabilities.zoom,
            torch: capabilities.torch,
            focusMode: capabilities.focusMode,
            focusDistance: capabilities.focusDistance,
            deviceId: settings.deviceId,
            facingMode: settings.facingMode,
        }

        const extendedSettings = settings as MediaTrackSettings & {
            focusMode?: string
            focusDistance?: number
        }

        debugInfo.value.currentSettings = {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            focusMode: extendedSettings.focusMode,
            focusDistance: extendedSettings.focusDistance,
            facingMode: settings.facingMode,
        }

        debugInfo.value.appliedConstraints = constraints as Record<string, unknown>
    }

    /**
     * Обновить настройки устройства
     */
    const updateDeviceInfo = (isMobile: boolean, isSecure: boolean) => {
        debugInfo.value.deviceInfo.isMobile = isMobile
        debugInfo.value.deviceInfo.isSecure = isSecure
    }

    /**
     * Добавить ошибку в лог
     */
    const addError = (error: string) => {
        debugInfo.value.errors.push(error)
    }

    return {
        debugInfo,
        updateCamerasList,
        updateSelectedCamera,
        updateDeviceInfo,
        addError,
    }
}
